import { FormMessages } from 'components/ui/form-messages';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from 'components/ui/command';
import { Input } from 'components/ui/input';
import { useDebounce } from '../../../hooks/use-debounce';
import { Delete, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { Command as CommandPrimitive } from 'cmdk';
import { useConfiguration } from '../../../context/configurationContext';

export interface AddressType {
  address1: string;
  address2: string;
  formattedAddress: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  lat: number;
  lng: number;
}

export const EMPTY_ADDRESS: AddressType = {
  address1: '',
  address2: '',
  formattedAddress: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  lat: 0,
  lng: 0,
};

// Direct API keys from env (REACT_APP_ vars are client-accessible in CRA)
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

interface GeocodingConfig {
  biasStrategy?: 'ip' | 'region' | 'ip-then-region';
  regionBounds?: { north: number; south: number; east: number; west: number };
  countryCodes?: string[];
}

// ─── Google Places API (New) ─────────────────────────────────────────

interface GooglePrediction {
  placePrediction: {
    placeId: string;
    place: string;
    text: { text: string };
  };
}

const GOOGLE_ADDRESS_TYPES = ['street_address', 'subpremise', 'route', 'street_number', 'landmark'];
const GOOGLE_PLACE_TYPES = ['(regions)'];

async function googleAutocomplete(
  input: string,
  geocoding?: GeocodingConfig,
  placeOnly?: boolean,
): Promise<GooglePrediction[]> {
  const strategy = geocoding?.biasStrategy;
  const bounds = geocoding?.regionBounds;
  const regionCodes = geocoding?.countryCodes ?? ['US'];

  const locationBiasMaybe =
    !placeOnly && bounds && (strategy === 'region' || strategy === 'ip-then-region')
      ? {
          locationBias: {
            rectangle: {
              low: { latitude: bounds.south, longitude: bounds.west },
              high: { latitude: bounds.north, longitude: bounds.east },
            },
          },
        }
      : {};

  const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': GOOGLE_KEY,
    },
    body: JSON.stringify({
      input,
      includedPrimaryTypes: placeOnly ? GOOGLE_PLACE_TYPES : GOOGLE_ADDRESS_TYPES,
      includedRegionCodes: regionCodes,
      ...locationBiasMaybe,
    }),
  });
  if (!res.ok) throw new Error(`Google autocomplete error: ${res.status}`);
  const data = await res.json();
  return data.suggestions || [];
}

async function googlePlaceDetails(placeId: string): Promise<{ address: AddressType; adrAddress: string }> {
  const res = await fetch(`https://places.googleapis.com/v1/${placeId}`, {
    headers: {
      'X-Goog-Api-Key': GOOGLE_KEY,
      'X-Goog-FieldMask': 'adrFormatAddress,shortFormattedAddress,formattedAddress,location,addressComponents',
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`Google place details error: ${res.status}`);
  const data = await res.json();

  const dataFinderRegx = (c: string) => {
    const regx = new RegExp(`<span class="${c}">([^<]+)<\\/span>`);
    const match = data.adrFormatAddress?.match(regx);
    return match ? match[1] : '';
  };

  return {
    address: {
      address1: dataFinderRegx('street-address'),
      address2: '',
      formattedAddress: data.formattedAddress || '',
      city: dataFinderRegx('locality'),
      region: dataFinderRegx('region'),
      postalCode: dataFinderRegx('postal-code'),
      country: dataFinderRegx('country-name'),
      lat: data.location?.latitude || 0,
      lng: data.location?.longitude || 0,
    },
    adrAddress: data.adrFormatAddress || '',
  };
}

// ─── Mapbox Geocoding v5 (via mapboxSdk already loaded by the template) ──────

// Cache features from autocomplete so place details doesn't need a second call
const mapboxFeatureCache = new Map<string, any>();

function getMapboxClient() {
  const w = window as any;
  if (!w.mapboxSdk || !w.mapboxgl?.accessToken) {
    throw new Error('Mapbox SDK not loaded');
  }
  return w.mapboxSdk({ accessToken: w.mapboxgl.accessToken });
}

const MAPBOX_PLACE_TYPES = ['place', 'locality', 'postcode', 'region', 'district'] as const;

async function mapboxAutocomplete(
  input: string,
  geocoding?: GeocodingConfig,
  placeOnly?: boolean,
): Promise<GooglePrediction[]> {
  const client = getMapboxClient();
  const strategy = geocoding?.biasStrategy;
  const bounds = geocoding?.regionBounds;
  const countries = (geocoding?.countryCodes ?? ['US']).map(c => c.toLowerCase());

  const proximityMaybe =
    !strategy || strategy === 'ip' || strategy === 'ip-then-region' ? { proximity: 'ip' } : {};
  const bboxMaybe =
    !placeOnly && bounds && (strategy === 'region' || strategy === 'ip-then-region')
      ? { bbox: [bounds.west, bounds.south, bounds.east, bounds.north] as [number, number, number, number] }
      : {};
  const typesMaybe = placeOnly ? { types: [...MAPBOX_PLACE_TYPES] as any } : {};

  const response = await client.geocoding
    .forwardGeocode({
      query: input,
      limit: 5,
      language: ['en'],
      countries,
      ...proximityMaybe,
      ...bboxMaybe,
      ...typesMaybe,
    })
    .send();

  const features = response.body.features || [];
  return features.map((feature: any) => {
    mapboxFeatureCache.set(feature.id, feature);
    return {
      placePrediction: {
        placeId: feature.id,
        place: feature.id,
        text: { text: feature.place_name },
      },
    };
  });
}

async function mapboxPlaceDetails(featureId: string): Promise<{ address: AddressType; adrAddress: string }> {
  const feature = mapboxFeatureCache.get(featureId);
  if (!feature) throw new Error('Place not found');

  const center = feature.center || [0, 0]; // [lng, lat]
  const context: any[] = feature.context || [];

  const getCtx = (type: string) => context.find((c: any) => c.id.startsWith(type));

  const address1 = [feature.address, feature.text].filter(Boolean).join(' ');
  const city = getCtx('place')?.text || getCtx('locality')?.text || '';
  const region = getCtx('region')?.short_code?.replace(/^US-|^CA-/, '') || getCtx('region')?.text || '';
  const postalCode = getCtx('postcode')?.text || '';
  const country = getCtx('country')?.text || '';
  const formattedAddress = feature.place_name || '';

  const adrParts: string[] = [];
  if (address1) adrParts.push(`<span class="street-address">${address1}</span>`);
  if (city) adrParts.push(`<span class="locality">${city}</span>`);
  if (region) adrParts.push(`<span class="region">${region}</span>`);
  if (postalCode) adrParts.push(`<span class="postal-code">${postalCode}</span>`);
  if (country) adrParts.push(`<span class="country-name">${country}</span>`);

  return {
    address: {
      address1,
      address2: '',
      formattedAddress,
      city,
      region,
      postalCode,
      country,
      lat: center[1],
      lng: center[0],
    },
    adrAddress: adrParts.join(', '),
  };
}

// ─── Mapbox Search Box API (POI-filtered autocomplete, e.g. schools) ─────

export async function mapboxSearchSuggest(
  input: string,
  categories: string[],
  sessionToken: string,
): Promise<GooglePrediction[]> {
  const params = new URLSearchParams({
    q: input,
    access_token: MAPBOX_TOKEN,
    types: 'poi',
    poi_category: categories.join(','),
    limit: '5',
    language: 'en',
    country: 'US,CA',
    proximity: 'ip',
    session_token: sessionToken,
  });
  const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/suggest?${params}`);
  if (!res.ok) throw new Error(`Mapbox Search suggest error: ${res.status}`);
  const data = await res.json();
  return (data.suggestions || []).map((s: any) => ({
    placePrediction: {
      placeId: s.mapbox_id,
      place: s.mapbox_id,
      text: { text: [s.name, s.place_formatted].filter(Boolean).join(', ') },
    },
  }));
}

export async function mapboxSearchRetrieve(
  mapboxId: string,
  sessionToken: string,
): Promise<{ address: AddressType; adrAddress: string; poiName?: string }> {
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    session_token: sessionToken,
  });
  const res = await fetch(
    `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?${params}`,
  );
  if (!res.ok) throw new Error(`Mapbox Search retrieve error: ${res.status}`);
  const data = await res.json();
  const feature = data.features?.[0];
  if (!feature) throw new Error('No feature returned');

  const props = feature.properties || {};
  const coords = props.coordinates || {};
  const ctx = props.context || {};

  const address1 = props.address || '';
  const city = ctx.place?.name || ctx.locality?.name || '';
  const region = ctx.region?.region_code || ctx.region?.name || '';
  const postalCode = ctx.postcode?.name || '';
  const country = ctx.country?.name || '';
  const formattedAddress = props.full_address || props.name || '';

  const adrParts: string[] = [];
  if (address1) adrParts.push(`<span class="street-address">${address1}</span>`);
  if (city) adrParts.push(`<span class="locality">${city}</span>`);
  if (region) adrParts.push(`<span class="region">${region}</span>`);
  if (postalCode) adrParts.push(`<span class="postal-code">${postalCode}</span>`);
  if (country) adrParts.push(`<span class="country-name">${country}</span>`);

  return {
    address: {
      address1,
      address2: '',
      formattedAddress,
      city,
      region,
      postalCode,
      country,
      lat: coords.latitude || 0,
      lng: coords.longitude || 0,
    },
    adrAddress: adrParts.join(', '),
    poiName: props.name || undefined,
  };
}

// ─── Unified fetchers (pick provider based on which key exists) ──────

async function fetchAutocomplete(
  input: string,
  poiCategories?: string[],
  sessionToken?: string,
  geocoding?: GeocodingConfig,
  placeOnly?: boolean,
): Promise<GooglePrediction[]> {
  if (poiCategories?.length && MAPBOX_TOKEN && sessionToken) {
    return mapboxSearchSuggest(input, poiCategories, sessionToken);
  }
  if (GOOGLE_KEY) return googleAutocomplete(input, geocoding, placeOnly);
  if (MAPBOX_TOKEN) return mapboxAutocomplete(input, geocoding, placeOnly);
  return [];
}

async function fetchPlaceDetails(
  placeId: string,
  poiCategories?: string[],
  sessionToken?: string,
): Promise<{ address: AddressType; adrAddress: string; poiName?: string }> {
  if (poiCategories?.length && MAPBOX_TOKEN && sessionToken) {
    return mapboxSearchRetrieve(placeId, sessionToken);
  }
  if (GOOGLE_KEY) return googlePlaceDetails(placeId);
  if (MAPBOX_TOKEN) return mapboxPlaceDetails(placeId);
  throw new Error('No map provider API key configured');
}

// ─── Component ───────────────────────────────────────────────────────

interface AddressAutoCompleteProps {
  address: AddressType;
  setAddress: (address: AddressType) => void;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  dialogTitle: string;
  showInlineError?: boolean;
  placeholder?: string;
  poiCategories?: string[];
  onPoiName?: (name: string) => void;
  placeOnly?: boolean;
}

export default function AddressAutoComplete(props: AddressAutoCompleteProps) {
  const {
    address,
    setAddress,
    dialogTitle,
    showInlineError = true,
    searchInput,
    setSearchInput,
    placeholder,
    poiCategories,
    onPoiName,
    placeOnly,
  } = props;

  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const sessionTokenRef = useRef(crypto.randomUUID());

  const sessionToken = poiCategories?.length ? sessionTokenRef.current : undefined;

  const { data, isLoading } = useSWR(
    selectedPlaceId === '' ? null : ['place-details', selectedPlaceId],
    () => fetchPlaceDetails(selectedPlaceId, poiCategories, sessionToken),
    { revalidateOnFocus: false },
  );

  const adrAddress = data?.adrAddress;

  useEffect(() => {
    if (data?.address) {
      setAddress(data.address);
      if (data.poiName && onPoiName) {
        onPoiName(data.poiName);
      }
      if (placeOnly) {
        setSelectedPlaceId('');
        setSearchInput('');
      }
    }
  }, [data, setAddress, onPoiName, placeOnly, setSearchInput]);

  if (placeOnly) {
    return (
      <AddressAutoCompleteInput
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        selectedPlaceId={selectedPlaceId}
        setSelectedPlaceId={setSelectedPlaceId}
        setIsOpenDialog={setIsOpen}
        showInlineError={false}
        placeholder={placeholder}
        poiCategories={poiCategories}
        sessionToken={sessionToken}
        placeOnly
      />
    );
  }

  return (
    <>
      {selectedPlaceId !== '' || address.formattedAddress ? (
        <div className="flex items-center gap-2">
          <Input value={address?.formattedAddress} readOnly />
          <Button
            type="reset"
            onClick={() => {
              setSelectedPlaceId('');
              setAddress(EMPTY_ADDRESS);
              setSearchInput('');
              sessionTokenRef.current = crypto.randomUUID();
            }}
            size="icon"
            variant="outline"
            className="shrink-0"
          >
            <Delete className="size-4" />
          </Button>
        </div>
      ) : (
        <AddressAutoCompleteInput
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          selectedPlaceId={selectedPlaceId}
          setSelectedPlaceId={setSelectedPlaceId}
          setIsOpenDialog={setIsOpen}
          showInlineError={showInlineError}
          placeholder={placeholder}
          poiCategories={poiCategories}
          sessionToken={sessionToken}
        />
      )}
    </>
  );
}

interface CommonProps {
  selectedPlaceId: string;
  setSelectedPlaceId: (placeId: string) => void;
  setIsOpenDialog: (isOpen: boolean) => void;
  showInlineError?: boolean;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  placeholder?: string;
  poiCategories?: string[];
  sessionToken?: string;
  placeOnly?: boolean;
}

function AddressAutoCompleteInput(props: CommonProps) {
  const {
    setSelectedPlaceId,
    selectedPlaceId,
    setIsOpenDialog,
    showInlineError,
    searchInput,
    setSearchInput,
    placeholder,
    poiCategories,
    sessionToken,
    placeOnly,
  } = props;

  const config = useConfiguration();
  const geocoding: GeocodingConfig | undefined = config?.geocoding;

  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const { data, isLoading } = useSWR(
    debouncedSearchInput ? ['autocomplete', debouncedSearchInput, poiCategories, placeOnly] : null,
    () => fetchAutocomplete(debouncedSearchInput, poiCategories, sessionToken, geocoding, placeOnly),
  );

  const predictions: GooglePrediction[] = data || [];

  return (
    <Command
      shouldFilter={false}
      onKeyDown={handleKeyDown}
      className="overflow-visible"
    >
      <div className={`flex w-full items-center justify-between rounded-md border border-input bg-background ring-offset-background text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0 ${placeOnly ? 'h-9' : ''}`}>
        <CommandPrimitive.Input
          value={searchInput}
          onValueChange={setSearchInput}
          onBlur={close}
          onFocus={open}
          placeholder={placeholder || 'Enter address'}
          className={`w-full ${placeOnly ? 'h-9' : 'h-10'} px-3 py-2 rounded-md outline-none text-sm placeholder:text-muted-foreground`}
        />
      </div>
      {searchInput !== '' && !isOpen && !selectedPlaceId && showInlineError && (
        <FormMessages
          type="error"
          className="pt-1 text-sm"
          messages={['Select a valid address from the list']}
        />
      )}

      {isOpen && (debouncedSearchInput.length > 0 || isLoading) && (
        <div className="relative animate-in fade-in-0 zoom-in-95 h-auto">
          <CommandList>
            <div className="absolute top-1.5 z-50 w-full">
              <CommandGroup className="relative h-auto z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md bg-background">
                {isLoading ? (
                  <div className="h-12 flex items-center justify-center">
                    <Loader2 className="size-5 animate-spin" />
                  </div>
                ) : (
                  <>
                    {predictions.map((prediction) => (
                      <CommandPrimitive.Item
                        value={prediction.placePrediction.text.text}
                        onSelect={() => {
                          setSearchInput('');
                          setSelectedPlaceId(prediction.placePrediction.place);
                          setIsOpenDialog(true);
                        }}
                        className="flex select-text flex-col cursor-pointer gap-0.5 h-max p-2 px-3 rounded-md aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-accent hover:text-accent-foreground items-start"
                        key={prediction.placePrediction.placeId}
                        onMouseDown={e => e.preventDefault()}
                      >
                        {prediction.placePrediction.text.text}
                      </CommandPrimitive.Item>
                    ))}
                  </>
                )}

                <CommandEmpty>
                  {!isLoading && predictions.length === 0 && debouncedSearchInput.length > 0 && (
                    <div className="py-3 px-3 text-sm text-muted-foreground">
                      No matches
                    </div>
                  )}
                </CommandEmpty>

              </CommandGroup>
            </div>
          </CommandList>
        </div>
      )}
    </Command>
  );
}
